// How a small business should create and warm up its brand accounts, the
// legitimate way: one real account per brand, following each platform's
// rules. Shown on the "Social accounts" tab. Researched 2026-10.

export const GENERAL_GUIDE = {
    title: 'Set up your brand accounts the right way',
    intro: 'New accounts start with little trust. Spend the first two weeks acting like a real customer of your niche, then post steadily. Platforms reward genuine activity and limit anything that looks automated or bought.',
    principles: [
        'Create one real account per brand and platform, run by real people on your team.',
        'Use the business’s own email and phone number, and turn on two-factor authentication.',
        'Complete the profile before you post: name, handle, photo, bio, category, contact and link.',
        'Keep the same name, handle and logo everywhere so customers can find and trust you.',
        'Spend the first days browsing, following and commenting in your niche like a normal customer.',
        'Start with a few posts a week and grow slowly; sudden spikes in activity look like spam.',
        'Keep links out of your first few posts; add them once the account has some history.',
        'Never buy followers or likes, join engagement pods, or use bots that follow, like or DM.',
        'Don’t run many accounts from one device, and don’t keep switching VPNs or locations.',
        'Scheduling through official APIs, as light speed does, is allowed; wait about a week before connecting it.',
        'If you get restricted, pause, read the notice, fix the cause and appeal through official help.',
    ],
};

export const SOCIAL_GUIDES = {
    instagram: {
        name: 'Instagram',
        accountType: 'Choose a Business account: it adds contact buttons and insights, and lets light speed publish through Instagram’s API.',
        create: [
            'Sign up in the Instagram app with your business email, not a personal one.',
            'Pick a handle that matches your brand name on your other platforms.',
            'Add your logo, a clear bio saying who you help, and your website link.',
            'Go to Settings, then Account type and tools, and switch to a Business account.',
            'Choose your business category and add your email, phone or address.',
            'Connect your Facebook Page if you have one, so both are managed together.',
            'Turn on two-factor authentication and confirm your email and phone.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Browse Reels and posts in your niche for 15–20 minutes a day.',
                    'Like and save posts you genuinely find useful.',
                    'Follow 5–10 local businesses, partners or creators you actually know of.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Leave a few thoughtful comments a day on posts in your niche.',
                    'Publish your first post or Reel introducing the business.',
                    'Reply to every comment and message you receive.',
                ],
            },
            {
                phase: 'Week 2',
                steps: [
                    'Post two or three times this week, mixing Reels and photos.',
                    'Share a few Stories showing your work or team behind the scenes.',
                    'Connect light speed and schedule posts through it from now on.',
                ],
            },
            {
                phase: 'Weeks 3–4',
                steps: [
                    'Settle into three or four posts a week at similar times.',
                    'Check insights weekly and repeat the formats that get saves and shares.',
                ],
            },
        ],
        cadence: 'Week 1: one post. Week 2: two or three. Weeks 3–4: three or four posts plus a few Stories each week.',
        avoid: [
            'Mass following and unfollowing, or dozens of follows in a short burst.',
            'Copy-pasted comments or the same message sent to many people.',
            'Bought followers, likes, engagement pods or bots of any kind.',
            'Changing your handle, bio and link repeatedly in the first weeks.',
            'Logging in from many devices, accounts or VPN locations at once.',
        ],
        links: [
            { label: 'Set up a business account', url: 'https://help.instagram.com/502981923235522' },
            { label: 'Add a Facebook Page to your Instagram account', url: 'https://help.instagram.com/399237934150902' },
        ],
    },
    facebook: {
        name: 'Facebook',
        accountType: 'Create a Facebook Page for the business, managed from your real personal profile; light speed posts to Pages.',
        create: [
            'Log in to your real personal Facebook profile; it stays private and only manages the Page.',
            'Create a new Page with your business name and the closest category.',
            'Add your logo as the profile picture and a simple cover image.',
            'Fill in the bio, website, phone, email, address and opening hours.',
            'Choose a Page username that matches your other handles.',
            'Add a teammate as a second admin so you never get locked out.',
            'Turn on two-factor authentication for everyone who manages the Page.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Invite friends, customers and staff who genuinely know the business to follow.',
                    'Publish a welcome post that explains what you offer and where.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Post one or two updates with your own photos or a short video.',
                    'Reply to every comment and message within a day.',
                    'Join or follow local and industry groups, and read before you post.',
                ],
            },
            {
                phase: 'Weeks 2–4',
                steps: [
                    'Connect light speed and schedule three posts a week.',
                    'Share helpful posts in groups only where the rules allow it.',
                    'Check Page insights weekly to see which posts reach people.',
                ],
            },
        ],
        cadence: 'Two posts in week 1, then about three a week, mixing photos, short videos and simple updates.',
        avoid: [
            'Creating a second personal profile just to run the Page.',
            'Posting the same link in many groups in a short time.',
            'Inviting large lists of strangers to like the Page.',
            'Buying Page likes or using engagement-for-engagement schemes.',
            'Posts that are only a link with no text, especially early on.',
        ],
        links: [
            { label: 'Create a Facebook Page for your business', url: 'https://www.facebook.com/business/help/473994396650734' },
            { label: 'Why Pages get limited', url: 'https://www.facebook.com/help/348805468517220' },
        ],
    },
    threads: {
        name: 'Threads',
        accountType: 'Create your Threads profile from your business Instagram account so the name, handle and audience carry over.',
        create: [
            'Finish setting up your business Instagram account first.',
            'Open the Threads app and log in with that Instagram account.',
            'Import your Instagram photo, bio and link, then edit the bio for Threads.',
            'Choose whether to follow the same accounts you follow on Instagram.',
            'Keep the profile public so new people can find your posts.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Read the feed and follow people in your industry and local area.',
                    'Reply to a few conversations with something genuinely useful.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Post a short introduction: who you are and what you will share.',
                    'Post one or two conversation starters or quick tips.',
                    'Answer every reply you get.',
                ],
            },
            {
                phase: 'Weeks 2–4',
                steps: [
                    'Connect light speed and schedule one short post most days.',
                    'Use topic tags that fit the post, one per post.',
                    'Keep replying to others; conversations matter more than broadcasts here.',
                ],
            },
        ],
        cadence: 'A few posts in week 1, then one post most days, plus daily replies to others.',
        avoid: [
            'Posting only links to your website or shop.',
            'Copying the same reply onto many people’s posts.',
            'Bursts of dozens of posts or follows in an hour.',
            'Follow-for-follow trades or bought followers.',
        ],
        links: [
            { label: 'Create a profile on Threads', url: 'https://help.instagram.com/1747515265645443/' },
            { label: 'About Threads', url: 'https://help.instagram.com/788669719351544/' },
        ],
    },
    tiktok: {
        name: 'TikTok',
        accountType: 'Use a Business account: you get analytics and the Commercial Music Library, which is licensed for brand content.',
        create: [
            'Sign up in the TikTok app with your business email or phone.',
            'Choose a handle that matches your brand on other platforms.',
            'Go to Settings, then Account, and switch to a Business account.',
            'Pick your business category and add a short, clear bio.',
            'Add your logo and, when the option is available, your website link.',
            'Turn on 2-step verification in Security settings.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Watch videos in your niche for 20–30 minutes a day, start to finish.',
                    'Like, save and follow accounts you genuinely enjoy.',
                    'Leave a few real comments that add to the conversation.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Post your first video, filmed on your phone, introducing the business.',
                    'Post one more video two or three days later.',
                    'Reply to comments, ideally with video replies.',
                ],
            },
            {
                phase: 'Week 2',
                steps: [
                    'Post every other day using trending formats that fit your niche.',
                    'Use only Commercial Music Library sounds in promotional videos.',
                    'Connect light speed once you have a few posts live.',
                ],
            },
            {
                phase: 'Weeks 3–4',
                steps: [
                    'Move to four or five videos a week.',
                    'Check which videos people watch to the end, and make more like them.',
                ],
            },
        ],
        cadence: 'Two videos in week 1, three or four in week 2, then four or five a week.',
        avoid: [
            'Reposting videos with other apps’ watermarks.',
            'Mass following, liking or commenting in short bursts.',
            'Running several accounts from one phone or switching VPNs.',
            'Buying followers or views, or using engagement groups.',
            'Deleting and reposting videos that didn’t take off.',
        ],
        links: [
            { label: 'Switch to a Business account', url: 'https://support.tiktok.com/en/using-tiktok/growing-your-audience/switching-to-a-creator-or-business-account' },
            { label: 'Create a TikTok account', url: 'https://support.tiktok.com/en/getting-started/creating-an-account' },
        ],
    },
    youtube: {
        name: 'YouTube',
        accountType: 'Create a channel under a brand name, not your personal name, so teammates can be given access later.',
        create: [
            'Sign in to YouTube with the Google account your business controls.',
            'Create a channel using your business name and a matching handle.',
            'Add your logo, a banner and a description with keywords customers search for.',
            'Add your website and contact email in the channel details.',
            'Verify your phone number to unlock longer videos and custom thumbnails.',
            'Turn on 2-Step Verification for the Google account.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Watch and subscribe to channels your customers already watch.',
                    'Leave a few genuine comments on videos in your niche.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Upload a short channel trailer or introduction video.',
                    'Post one or two Shorts showing your product or service.',
                    'Write clear titles and descriptions; avoid clickbait.',
                ],
            },
            {
                phase: 'Weeks 2–4',
                steps: [
                    'Connect light speed and schedule two or three Shorts a week.',
                    'Add one longer how-to or FAQ video in weeks 3–4.',
                    'Organize videos into playlists and reply to every comment.',
                ],
            },
        ],
        cadence: 'One or two Shorts a week at first, then two or three Shorts plus one longer video a month.',
        avoid: [
            'Uploading many videos in one day on a brand-new channel.',
            'Reusing other creators’ clips or music you don’t have rights to.',
            'Misleading titles, thumbnails or stuffed tags.',
            'Buying views or subscribers, or sub-for-sub swaps.',
        ],
        links: [
            { label: 'Get access to intermediate and advanced features', url: 'https://support.google.com/youtube/answer/9891124' },
            { label: 'Verify your YouTube account', url: 'https://support.google.com/youtube/answer/171664' },
        ],
    },
    linkedin: {
        name: 'LinkedIn',
        accountType: 'Create a Company Page for the business, run from your real personal profile, and post from both.',
        create: [
            'Complete your personal profile with your real name, photo and current role at the business.',
            'Confirm a work email on your own domain if LinkedIn asks to verify you.',
            'Create a Company Page on desktop with the business name and website.',
            'Add your logo, cover image, tagline, industry, size and location.',
            'Add a teammate as a second Page admin.',
            'Turn on two-step verification for every admin.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Ask employees to list the business in their Experience section.',
                    'Invite connections who genuinely know you to follow the Page.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Publish a welcome post on the Page about what you do.',
                    'Reshare it from your personal profile with a short personal note.',
                    'Comment thoughtfully on a few posts from customers or partners.',
                ],
            },
            {
                phase: 'Weeks 2–4',
                steps: [
                    'Connect light speed and schedule two or three posts a week.',
                    'Mix tips, customer stories and behind-the-scenes posts.',
                    'Send personalized connection requests only to people you know or met.',
                ],
            },
        ],
        cadence: 'One or two posts in week 1, then two or three a week on the Page and your profile.',
        avoid: [
            'Sending large numbers of generic connection requests or messages.',
            'Creating a fake profile to manage the Page.',
            'Engagement pods that trade likes and comments.',
            'Posting identical text to the Page and your profile at the same time.',
        ],
        links: [
            { label: 'Create a LinkedIn Page', url: 'https://www.linkedin.com/help/linkedin/answer/a543852' },
            { label: 'Troubleshoot creating a Page', url: 'https://www.linkedin.com/help/linkedin/answer/a726850' },
        ],
    },
    x: {
        name: 'X',
        accountType: 'Use a standard account in your brand’s name; paid verification is optional and not needed to start.',
        create: [
            'Sign up with your business email and phone number.',
            'Choose a handle that matches your brand on other platforms.',
            'Add your logo, a header image, a clear bio and your website.',
            'Set your location and confirm both email and phone.',
            'Turn on two-factor authentication in Security settings.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Follow 10–20 relevant people, customers and industry accounts, a few at a time.',
                    'Read your feed daily and like posts you find useful.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Post an introduction and pin it to your profile.',
                    'Reply helpfully to a few conversations in your niche each day.',
                    'Post one or two short tips or updates.',
                ],
            },
            {
                phase: 'Weeks 2–4',
                steps: [
                    'Connect light speed and schedule one post most days.',
                    'Keep links to a minority of posts; most should stand on their own.',
                    'Keep replying daily; replies build reach faster than posts.',
                ],
            },
        ],
        cadence: 'Two or three posts in week 1, then about one a day plus a few replies.',
        avoid: [
            'Follow churn: following many accounts and unfollowing those who don’t follow back.',
            'Automated or copy-paste replies to people who didn’t ask.',
            'Posting the same or near-identical text repeatedly.',
            'Using trending hashtags that have nothing to do with your post.',
            'Running several accounts to boost each other.',
        ],
        links: [
            { label: 'Automation rules', url: 'https://help.x.com/en/rules-and-policies/x-automation' },
            { label: 'Authenticity policy', url: 'https://help.x.com/en/rules-and-policies/authenticity' },
        ],
    },
    pinterest: {
        name: 'Pinterest',
        accountType: 'Use a free Pinterest business account: it adds analytics and lets you claim your website for credit on every Pin.',
        create: [
            'Sign up for a business account with an email not used on another Pinterest account.',
            'Add your business name, logo, a short description and your website.',
            'Claim your website by adding the tag or file Pinterest gives you.',
            'Create five to ten boards named with words your customers search for.',
            'Turn on two-factor authentication in Security settings.',
        ],
        warmup: [
            {
                phase: 'Days 1–3',
                steps: [
                    'Search your niche and save good Pins from others to your boards.',
                    'Follow a few relevant brands, creators and boards.',
                ],
            },
            {
                phase: 'Days 4–7',
                steps: [
                    'Publish your first few Pins with your own vertical images.',
                    'Write descriptive titles and descriptions with natural keywords.',
                ],
            },
            {
                phase: 'Weeks 2–4',
                steps: [
                    'Connect light speed and schedule a few fresh Pins every week.',
                    'Link Pins to the most relevant page, not always your homepage.',
                    'Check analytics for Pins that earn saves and outbound clicks.',
                ],
            },
        ],
        cadence: 'Three to five Pins in week 1, then one or two fresh Pins most days.',
        avoid: [
            'Saving the same Pin to many boards in a short time.',
            'Pinning dozens of times in a burst, which can trigger rate-limit blocks.',
            'Links to pages that don’t match the image or redirect elsewhere.',
            'Creating extra accounts to promote your own Pins.',
        ],
        links: [
            { label: 'Get a business account', url: 'https://help.pinterest.com/en/business/article/get-a-business-account' },
            { label: 'Claim your website', url: 'https://help.pinterest.com/en/business/article/claim-your-website' },
            { label: 'Prevent rate limit blocks', url: 'https://help.pinterest.com/en/article/rate-limit-blocks' },
        ],
    },
};
